// file: CustomDropdown.jsx
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
  memo,
} from "react";
import styles from "./customDropdown.module.scss";
import { PackageIcon } from "../../../utils/common/packageIcon";

// item.icon may be an image URL (project logos) or a Google icon name /
// SVG code (roles) — render the right thing for each.
const isImageUrl = (value = "") => /^(https?:|data:|blob:|\/)/i.test(String(value).trim());

const OptionIcon = ({ icon, alt, className }) =>
  isImageUrl(icon) ? (
    <img src={icon} alt={alt} className={className} />
  ) : (
    <PackageIcon iconHtml={icon} title={alt} className={className} />
  );


const ArrowIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const truncateDisplayText = (text = "", maxLength = 30) => {
  const value = String(text ?? "");

  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength).trimEnd()}...`;
};

// ── Single Row ──
const DropdownOption = memo(({ item, selected, onPress, displayKey, variant }) => {
  const label = item?.[displayKey] ?? "";

  if (variant === "taskFilter") {
    return (
      <div
        className={`${styles.filterOption} ${selected ? styles.filterOptionSelected : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onPress(item);
        }}
      >
        <div className={`${styles.checkbox} ${selected ? styles.checkboxSelected : ""}`}>
          {selected && (
            <svg
              className={styles.checkIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>
        {/* {item?.icon && (
          <OptionIcon icon={item.icon} alt={label} className={styles.optionIcon} />
        )} */}
        <span className={styles.filterOptionText} title={label}>
          {truncateDisplayText(label, 30)}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`${styles.option} ${selected ? styles.selected : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onPress(item);
      }}
    >
      <div className={styles.indicator} />
      {item?.icon && (
        <OptionIcon icon={item.icon} alt={label} className={styles.optionIcon} />
      )}
      <span className={styles.optionText} title={label}>
        {truncateDisplayText(label, 30)}
      </span>
    </div>
  );
});

// ── Main Component ──
const CustomDropdown = ({
  label,
  value,
  data = [],
  onSelect,
  displayKey = "label",
  searchEnabled = false,
  disabled = false,
  placeholder = "",
  emptyMessage = "No results found",
  iconProps = false,
  showArrow = false,
  onFocus,
  labelClassName = "",
  triggerClassName = "",
  className = "",
  variant = "",
}) => {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownIdRef = useRef(Math.random().toString(36).slice(2));

  // Sync search display to selected value when closed
  const selectedItem = useMemo(() => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "object") {
      // value may be a partial {id, name} — find the full record for the icon
      return data.find((d) => String(d?.id) === String(value?.id)) || null;
    }
    return data.find((d) => String(d?.id) === String(value)) || null;
  }, [value, data]);

  // Plain display string (used by searchEnabled input)
  const displayValue = useMemo(() => {
    if (!selectedItem) return "";
    return truncateDisplayText(selectedItem[displayKey] || "", 30);
  }, [selectedItem, displayKey]);

  useEffect(() => {
    if (!open) {
      setSearch(displayValue);
    } else {
      setSearch("");
      window.dispatchEvent(
        new CustomEvent("dropdown-opened", { detail: { id: dropdownIdRef.current } })
      );
    }
  }, [open, displayValue]);

  useEffect(() => {
    const handleDropdownOpened = (e) => {
      if (e.detail?.id !== dropdownIdRef.current) {
        setOpen(false);
      }
    };
    window.addEventListener("dropdown-opened", handleDropdownOpened);
    return () => window.removeEventListener("dropdown-opened", handleDropdownOpened);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredData = useMemo(() => {
    if (!searchEnabled || !search?.trim?.()) return data;
    const q = search.toLowerCase();
    return data.filter((item) =>
      String(item[displayKey] ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [search, data, displayKey, searchEnabled]);

  const noDataMessage = data.length === 0 ? emptyMessage : "No results found";

  const handleToggle = useCallback(() => {
    if (disabled) return;
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        setSearch("");
        onFocus?.();
      }
      return next;
    });
  }, [disabled, onFocus]);

  const handleSelect = useCallback(
    (item) => {
      setOpen(false);
      setSearch("");
      if (variant === "taskFilter") {
        let currentValues = Array.isArray(value)
          ? [...value]
          : value !== undefined && value !== null
          ? [typeof value === "object" ? value.id : value]
          : [];

        if (item.id === "all") {
          onSelect?.(["all"]);
          return;
        }

        currentValues = currentValues.filter((id) => id !== "all");

        if (currentValues.some((id) => String(id) === String(item.id))) {
          currentValues = currentValues.filter((id) => String(id) !== String(item.id));
        } else {
          currentValues.push(item.id);
        }

        if (currentValues.length === 0) {
          currentValues = ["all"];
        }

        onSelect?.(currentValues);
      } else {
        onSelect?.(item);
      }
    },
    [onSelect, variant, value],
  );

  const floated = open || selectedItem !== null;

  if (variant === "taskFilter") {
    const selectedList = Array.isArray(value)
      ? value
      : value !== undefined && value !== null
      ? [typeof value === "object" ? value.id : value]
      : [];

    const nonAllSelected = selectedList.filter((id) => id !== "all");
    let filterDisplayLabel = placeholder || label || "Select";

    if (selectedList.includes("all") || nonAllSelected.length === 0) {
      filterDisplayLabel = placeholder || label || "Select";
    } else if (nonAllSelected.length === 1) {
      const singleId = nonAllSelected[0];
      const matched = data.find((d) => String(d.id) === String(singleId));
      filterDisplayLabel = matched ? matched[displayKey] : (placeholder || "1 Selected");
    } else {
      filterDisplayLabel = `${nonAllSelected.length} Selected`;
    }

    const singleSelectedItem =
      nonAllSelected.length === 1
        ? data.find((d) => String(d.id) === String(nonAllSelected[0]))
        : null;

    return (
      <div className={`${styles.wrap} ${styles.taskFilterWrap} ${className}`} ref={wrapRef}>
        <button
          type="button"
          className={`${styles.taskFilterBox} ${open ? styles.taskFilterBoxOpen : ""} ${
            disabled ? styles.disabled : ""
          } ${triggerClassName}`}
          onClick={handleToggle}
        >
          {singleSelectedItem?.icon && (
            <OptionIcon
              icon={singleSelectedItem.icon}
              alt={filterDisplayLabel}
              className={styles.triggerIcon}
            />
          )}
          <span className={styles.taskFilterLabel}>{filterDisplayLabel}</span>
          <ArrowIcon className={styles.taskFilterArrow} />
        </button>

        {open && (
          <div className={`${styles.list} ${styles.taskFilterList}`}>
            {filteredData.length === 0 ? (
              <div className={styles.noData}>{noDataMessage}</div>
            ) : (
              filteredData.map((item) => {
                const isSelected = selectedList.some((id) => String(id) === String(item.id));
                return (
                  <DropdownOption
                    key={item.id}
                    item={item}
                    selected={isSelected}
                    onPress={handleSelect}
                    displayKey={displayKey}
                    variant={variant}
                  />
                );
              })
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${styles.wrap} ${className}`} ref={wrapRef}>
      {/* Floating label */}
      {Boolean(label) && (
        <label
          className={`${styles.label} ${floated ? styles.floated : ""} ${labelClassName}`}
        >
          {label}
        </label>
      )}

      {/* Input box */}
      <div
        className={`${styles.box} ${open ? styles.focused : ""} ${
          disabled ? styles.disabled : ""
        } ${triggerClassName}`}
        onClick={handleToggle}
      >
        {searchEnabled ? (
          <input
            type="text"
            className={styles.searchInput}
            value={open ? search : displayValue}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={open ? "Search..." : ""}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              if (!open) {
                setOpen(true);
                setSearch("");
              }
            }}
            autoComplete="off"
          />
        ) : (
          <span
            className={`${styles.value} ${!displayValue ? styles.placeholder : ""}`}
          >
            {selectedItem?.icon && (
              <div className={styles.triggerContent}>
                <OptionIcon
                  icon={selectedItem.icon}
                  alt={selectedItem?.[displayKey]}
                  className={styles.triggerIcon}
                />
              </div>
            )}
            {displayValue || placeholder}
          </span>
        )}

        {/* Divider */}
        {/* <div className={styles.divider} /> */}

        {/* Arrow / Search icon */}
        <div
          // className={`${styles.iconBtn} ${open && styles.rotated}`}
          className={`${styles.iconBtn} ${open && (!searchEnabled || showArrow) ? styles.rotated : ""}`}
        >
          {/* <ArrowIcon /> */}
          {searchEnabled && !showArrow ? <SearchIcon /> : <ArrowIcon />}
        </div>
      </div>

      {/* List */}
      {open && (
        <div className={styles.list}>
          {filteredData.length === 0 ? (
            <div className={styles.noData}>{noDataMessage}</div>
          ) : (
            filteredData.map((item) => {
              const isSelected =
                typeof value === "object"
                  ? value?.id === item.id
                  : value === item.id;
              return (
                <DropdownOption
                  key={item.id}
                  item={item}
                  selected={isSelected}
                  onPress={handleSelect}
                  displayKey={displayKey}
                />
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default memo(CustomDropdown);
